import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, XCircle, X } from 'lucide-react';
import { cn } from '../../lib/utils';

interface FormFieldProps {
  label: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}

export function FormField({ label, error, required, children, className }: FormFieldProps) {
  return (
    <div className={cn('space-y-1', className)}>
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

export function Input({ className, ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={cn(
        'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition-colors focus:border-primary-500 focus:ring-1 focus:ring-primary-500/30 disabled:bg-gray-50 disabled:text-gray-500',
        className
      )}
      {...props}
    />
  );
}

export function Select({ className, children, ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={cn(
        'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition-colors focus:border-primary-500 focus:ring-1 focus:ring-primary-500/30 disabled:bg-gray-50 disabled:text-gray-500',
        className
      )}
      {...props}
    >
      {children}
    </select>
  );
}

export function Textarea({ className, ...props }: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={cn(
        'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition-colors focus:border-primary-500 focus:ring-1 focus:ring-primary-500/30 disabled:bg-gray-50 disabled:text-gray-500',
        className
      )}
      rows={3}
      {...props}
    />
  );
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'sm' | 'md';
}

export function Button({ variant = 'primary', size = 'md', className, children, ...props }: ButtonProps) {
  const base = 'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';
  const variants = {
    primary: 'bg-primary-600 text-white hover:bg-primary-500 focus:ring-primary-500 shadow-sm',
    secondary: 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-gray-300',
    danger: 'bg-red-600 text-white hover:bg-red-500 focus:ring-red-500 shadow-sm',
    ghost: 'text-gray-600 hover:bg-gray-100 focus:ring-gray-300',
  };
  const sizes = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
  };
  return (
    <button className={cn(base, variants[variant], sizes[size], className)} {...props}>
      {children}
    </button>
  );
}

export function Toast({
  message,
  type = 'success',
  onClose,
  duration = 15000,
}: {
  message: string;
  type?: 'success' | 'error';
  onClose: () => void;
  /** Auto-dismiss after this many ms. Pass 0 to disable. Defaults to 15 s. */
  duration?: number;
}) {
  const isSuccess = type === 'success';
  const Icon = isSuccess ? CheckCircle2 : XCircle;
  const title = isSuccess ? 'Success' : 'Error';

  // Auto-dismiss, pause on hover
  const [paused, setPaused] = useState(false);
  const remainingRef = useRef(duration);
  const startedAtRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!duration || paused) return;
    startedAtRef.current = Date.now();
    const t = setTimeout(onClose, remainingRef.current);
    return () => {
      clearTimeout(t);
      remainingRef.current -= Date.now() - startedAtRef.current;
    };
  }, [paused, duration, onClose]);

  return (
    <div
      role="alert"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className={cn(
        'fixed top-6 right-6 z-50 w-full max-w-md overflow-hidden rounded-xl border shadow-xl',
        'animate-[slideIn_.25s_ease-out] ring-1',
        isSuccess
          ? 'border-emerald-300 bg-emerald-50 ring-emerald-200'
          : 'border-red-300 bg-red-50 ring-red-200'
      )}
    >
      {/* Accent strip */}
      <div className={cn('h-1 w-full', isSuccess ? 'bg-emerald-500' : 'bg-red-500')} />
      <div className="flex items-start gap-3 p-4">
        <div
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
            isSuccess ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
          )}
        >
          <Icon size={20} strokeWidth={2.25} />
        </div>
        <div className="min-w-0 flex-1">
          <p className={cn('mb-0.5 text-sm font-semibold', isSuccess ? 'text-emerald-900' : 'text-red-900')}>
            {title}
          </p>
          <p className={cn('whitespace-pre-line break-words text-sm leading-snug', isSuccess ? 'text-emerald-800' : 'text-red-800')}>
            {message}
          </p>
        </div>
        <button
          onClick={onClose}
          aria-label="Dismiss"
          className={cn(
            'shrink-0 rounded-full p-1 transition-colors',
            isSuccess
              ? 'text-emerald-500 hover:bg-emerald-100 hover:text-emerald-700'
              : 'text-red-500 hover:bg-red-100 hover:text-red-700'
          )}
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
